import { motion, useScroll, useTransform } from "framer-motion";
import { scrollToId } from "../lib/scroll";
import { IMAGES } from "../data/content";

const EASE = [0.22, 1, 0.36, 1];
const LINES = ["MAKEUP THAT", "FEELS LIKE", "YOU."];

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImg = useTransform(scrollY, [0, 900], [0, 170]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.35]);

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <motion.div style={{ y: yImg }} className="absolute inset-0 -bottom-44">
        <img
          src={IMAGES.hero}
          alt="Bride with luminous, natural bridal makeup by Mrinalee"
          className="w-full h-full object-cover object-[center_22%] sm:object-[center_48%]"
          fetchPriority="high"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#FBF6EE] via-[#FBF6EE]/45 to-transparent" aria-hidden="true" />
      <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-[#FBF6EE]/70 to-transparent" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#FBF6EE]/55 via-transparent to-transparent" aria-hidden="true" />

      <motion.div style={{ opacity }} className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-20 sm:pb-28 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8, ease: EASE }}
          className="inline-flex items-center gap-2.5 border border-[#2A1F16]/12 bg-[#FFFCF7]/78 backdrop-blur-md rounded-full px-4 py-2 mb-8"
          data-testid="hero-availability-badge"
        >
          <span className="availability-dot h-1.5 w-1.5 rounded-full bg-[#B8893B]" aria-hidden="true" />
          <span className="text-[11px] sm:text-[11px] uppercase tracking-[0.2em] text-[#6B5A49]">
            Now accepting bookings for bridal &amp; special occasions
          </span>
        </motion.div>

        <h1
          data-testid="hero-headline"
          className="font-serif-ed font-normal text-[#2A1F16] text-[13.5vw] sm:text-7xl lg:text-[6.5rem] leading-[0.98] tracking-tight"
        >
          {LINES.map((line, i) => (
            <span key={line} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${i === 2 ? "italic text-[#B8893B]" : ""}`}
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ delay: 0.35 + i * 0.16, duration: 1.05, ease: EASE }}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
          className="mt-5 text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#2A1F16] font-medium"
          data-testid="hero-subheadline"
        >
          Bridal <span className="text-[#B8893B]">•</span> Occasion <span className="text-[#B8893B]">•</span> Editorial Makeup
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.08, duration: 0.8, ease: EASE }}
          className="mt-5 max-w-md text-sm sm:text-base leading-relaxed text-[#6B5A49]"
        >
          Thoughtfully crafted makeup looks designed to enhance your features, complement your
          style, and make every moment feel unforgettable.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8, ease: EASE }}
          className="mt-9 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-enquire-button"
            onClick={() => scrollToId("inquire")}
            className="group text-[11px] uppercase tracking-[0.25em] bg-[#C9A25F] text-[#2A1F16] px-8 py-4 rounded-full hover:bg-[#B8893B] transition-colors duration-400"
          >
            Enquire For Your Date
          </button>
          <button
            data-testid="hero-portfolio-button"
            onClick={() => scrollToId("services")}
            className="text-[11px] uppercase tracking-[0.25em] border border-[#2A1F16]/30 text-[#2A1F16] px-8 py-4 rounded-full hover:border-[#B8893B] hover:bg-[#2A1F16] hover:text-[#FBF6EE] transition-colors duration-400"
          >
            View Services
          </button>
        </motion.div>
      </motion.div>
    </section>
  );
};
